import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../client";
import type {
  OrganizationContact,
  OrganizationContactCreate,
  OrganizationContactUpdate,
} from "../types";

const ORG_CONTACTS_KEY = (orgId: string) =>
  ["organizations", orgId, "contacts"] as const;
const CONTACT_KEY = (id: string) => ["organization-contacts", id] as const;

export function useOrgContacts(orgId: string | undefined) {
  return useQuery<OrganizationContact[]>({
    queryKey: orgId
      ? ORG_CONTACTS_KEY(orgId)
      : ["organizations", "_", "contacts"],
    queryFn: () => apiRequest(`/api/organizations/${orgId}/contacts`),
    enabled: Boolean(orgId),
  });
}

export function useCreateOrgContact(orgId: string) {
  const qc = useQueryClient();
  return useMutation<OrganizationContact, Error, OrganizationContactCreate>({
    mutationFn: (payload) =>
      apiRequest(`/api/organizations/${orgId}/contacts`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ORG_CONTACTS_KEY(orgId) }),
  });
}

export function useUpdateOrgContact(id: string, orgId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<OrganizationContact, Error, OrganizationContactUpdate>({
    mutationFn: (payload) =>
      apiRequest(`/api/organization-contacts/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    onSuccess: (contact) => {
      qc.setQueryData(CONTACT_KEY(id), contact);
      if (orgId)
        qc.invalidateQueries({ queryKey: ORG_CONTACTS_KEY(orgId) });
    },
  });
}

export function useDeleteOrgContact(orgId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiRequest(`/api/organization-contacts/${id}`, { method: "DELETE" }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ORG_CONTACTS_KEY(orgId) }),
  });
}
